/**
 * Protocole WebSocket entre les telephones et le Durable Object de la famille.
 *
 * Toute mutation porte un `ref` genere par le client. Le serveur repond
 * `ok`/`rejet` avec ce meme `ref`, ce qui permet au client de confirmer ou
 * d'annuler sa mise a jour optimiste sans ambiguite, meme si plusieurs
 * mutations sont en vol simultanement.
 */

import type { BrouillonItem, ChangementItem, Item } from './item';

/** Client -> serveur. */
export type MsgClient =
  | { t: 'bonjour'; depuis?: string | null }
  | { t: 'ping' }
  | { t: 'item.creer'; ref: string; brouillon: BrouillonItem }
  | { t: 'item.maj'; ref: string; id: string; champs: ChangementItem };

/** Serveur -> client. */
export type MsgServeur =
  /** Etat complet (a la connexion) ou delta (si `depuis` etait fourni). */
  | { t: 'sync'; items: Item[]; complet: boolean; maintenant: string }
  /** Un item a change chez quelqu'un d'autre. Upsert cote client. */
  | { t: 'item'; item: Item; par: string }
  /**
   * Items retires definitivement de la base (menage des anciennes taches
   * faites). Distinct de `statut: 'annule'`, qui est une suppression
   * utilisateur conservant une pierre tombale : ici la ligne n'existe plus du
   * tout. Un client plus ancien qui ignore ce message se resynchronisera
   * simplement a sa prochaine reconnexion.
   */
  | { t: 'item.retire'; ids: string[] }
  /** La mutation `ref` est acceptee ; `item` fait autorite. */
  | { t: 'ok'; ref: string; item: Item }
  /** La mutation `ref` est refusee ; `item` (si present) fait autorite. */
  | { t: 'rejet'; ref: string; raison: CodeRejet; message: string; item?: Item }
  | { t: 'pong'; maintenant: string };

export type CodeRejet =
  | 'introuvable'
  | 'invalide'
  | 'conflit'
  | 'interdit'
  | 'interne';

/** Delai au-dela duquel le client annule une mutation restee sans reponse. */
export const DELAI_MUTATION_MS = 6000;

/** Intervalle de ping applicatif : garde le socket vivant a travers les NAT. */
export const INTERVALLE_PING_MS = 25000;

export function estMsgServeur(v: unknown): v is MsgServeur {
  return typeof v === 'object' && v !== null && typeof (v as { t?: unknown }).t === 'string';
}
